import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Constructor, GroupLockRequestResultDto } from 'webcommon/shared';
import { ApBaseComponent } from '../ap-base-component/index';
import { ApConfirmModalService } from '../ap-confirm/index';
import { ApLockService } from './ap-locking.service';

export function ApLockingComponent<T extends Constructor<ApBaseComponent>>(Base: T) {
  return class extends Base {
    loadDate: string;
    currentLock: GroupLockRequestResultDto | null;

    // These will need to be injected into child component
    confirmModalService: ApConfirmModalService;
    lockService: ApLockService;

    locking = false;

    displayOnLockErrorModal = true;
    set DisplayOnLockErrorModal(value: boolean) {
      this.displayOnLockErrorModal = value;
    }

    constructor(...args: any[]) {
      super(...args);
    }

    load(): void {
      this.updateLoadDate().pipe(
        takeUntil(this.onDestroy$),
      ).subscribe(
        () => {/*success*/},
        () => {/*error*/}, // Eat error
      );
    }

    updateLoadDate(): Observable<string> {
      if (!this.lockService) { console.error('Inject - lockService: ApLockService'); }

      return this.lockService.getDatabaseTime().pipe(
        tap((date) => this.loadDate = date),
      );
    }

    WithLock<OT>(
      visitId: string,
      lockType: string,
      func: () => Observable<OT>,
      assumeChangesOnError: boolean = true,
      hasChanges: (value: OT) => boolean = () => true): Observable<OT> {
      return this.Lock(visitId, lockType).pipe(
        switchMap(() => func()),
        switchMap((data) => this.ReleaseLock(hasChanges(data)).pipe(
          map(() => data),
        )),
        catchError((error) => {
          return this.ReleaseLock(assumeChangesOnError).pipe(
            switchMap(() => throwError(error)),
          );
        }),
      );
    }

    Lock(visitId: string, lockType: string): Observable<boolean> {
      let errorText = '';
      if (!this.loadDate) {
        errorText = 'Child component must implement and call load() and load() must call super.load()';
      }
      if (this.locking) {
        errorText = 'Already locking - Only 1 lock can be obtained at a time per component.';
      }
      if (errorText) {
        console.error(errorText);
        return new Observable<boolean>((observer) => {
          observer.error(errorText);
          observer.complete();
        });
      }

      this.locking = true;
      return this.lockService.getLock(visitId, lockType, this.loadDate).pipe(
        catchError((error) => this.onLockError(error)), // Don't continue, lock failed,
        switchMap((data) => this.onLockSuccess(data)),
        finalize(() => this.locking = false),
      );
    }

    ReleaseLock(hasChanges: boolean = true): Observable<boolean> {
      if (!this.currentLock) {
        return of(false);
      }
      const curLock = this.currentLock;
      const lockObs = hasChanges
        ? this.lockService.releaseLock(curLock.GroupInstanceId)
        : this.lockService.deleteLock(curLock.GroupInstanceId);
      return lockObs.pipe(
        switchMap(() => this.onReleaseLockSuccess()),
      );
    }

    onLockSuccess(data: GroupLockRequestResultDto): Observable<boolean> {
      this.currentLock = data;
      return of(true);
    }

    onReleaseLockSuccess(): Observable<boolean> {
      this.currentLock = null;
      return this.updateLoadDate().pipe(
        map(() => true),
      );
    }

    onLockError(error: HttpErrorResponse): Observable<GroupLockRequestResultDto> {
      if (!error || !error.error) {
        return throwError(error);
      }

      if (!this.displayOnLockErrorModal) {
        return throwError(error);
      }

      const lockDto: GroupLockRequestResultDto = error.error as GroupLockRequestResultDto;
      if (lockDto.RefreshNeeded) {
        this.confirmModalService.openModal(
          'lock-failed-refresh-data-confirmation',
          'The content you\'re editing may have been changed outside of your session. Would you like to reload this tile now?',
          'Wait!',
          'Reload',
        ).then((reload) => {
          if (reload) {
            this.load();
          }
        });
      } else {
        const set = new Set(lockDto.LockList.map((lock) => lock.LockedByName));
        const array = Array.from(set);
        const usernames = [...array].join(', ');
        this.confirmModalService.openModal(
          'existing-lock-notification',
          `This content is currently locked by user(s) ${usernames}. Maybe try again later.`,
          'Oops!',
          'OK',
          null,
          null,
          null,
          true,
        ).then(() => {
          // Don't care about response
        });
      }
      return throwError(error);
    }

    public getLockedUserMessage(lockDto: GroupLockRequestResultDto): string | null {
      if (!lockDto || !lockDto.LockList || lockDto.LockList.length === 0) return null;
      const set = new Set(lockDto.LockList.map((lock) => lock.LockedByName));
      const array = Array.from(set);
      const usernames = [...array].join(', ');
      return `This content is currently locked by user(s) ${usernames}. Please try again later.`;
    }

    override ngOnDestroy() {
      const timeout$ = timer(1000);
      this.ReleaseLock().pipe(
        finalize(() => super.ngOnDestroy()),
        takeUntil(timeout$),
      ).subscribe();
    }
  };
}
